import {
    CART_ADD_ITEM, CART_REMOVE_ITEM, CART_REMOVE_ALL, CART_UPDATE_QUANTITIES,
    CART_REMOVE_EXPIRED, CART_SET_USERDATA
} from './cartActionTypes';

/**
 * @param {array} items
 * @param {object} product
 * @param {array} options
 * @return {number}
 */
function findItemIndex(items, product, options) {
    return items.findIndex((item) => {
        if (item.product.productID !== product.productID) {
            return false;
        }
        if (options) {
            if (item.option.optionID !== options.optionID) {
                return false;
            }
        }
        return true;
    });
}

function calcSubtotal(items) {
    return items.reduce((subtotal, item) => subtotal + item.total, 0);
}

function calcQuantity(items) {
    return items.reduce((quantity, item) => quantity + item.quantity, 0);
}

function calcTotal(subtotal) {
    return subtotal;
}

function addItem(state, product, option, quantity, isAvailable) {
    const itemIndex = findItemIndex(state.items, product, option);

    let newItems;
    let { lastItemId } = state;
    if (itemIndex === -1) {
        lastItemId += 1;
        newItems = [...state.items, {
            id: lastItemId,
            product: { ...product },
            option: { ...option },
            quantity,
            isAvailable
        }];
    } else {
        const item = state.items[itemIndex];

        newItems = [
            ...state.items.slice(0, itemIndex),
            {
                ...item,
                quantity: item.quantity + quantity,
                isAvailable,
            },
            ...state.items.slice(itemIndex + 1),
        ];
    }

    return {
        ...state,
        lastItemId,
        items: newItems,
        quantity: calcQuantity(newItems),
    };
}

function removeItem(state, itemId) {
    const { items } = state;
    const newItems = items.filter((item) => item.id !== itemId);

    const subtotal = calcSubtotal(newItems);
    const total = calcTotal(subtotal);

    return {
        ...state,
        items: newItems,
        quantity: calcQuantity(newItems),
        subtotal,
        total,
    };
}

function removeExpiredItems(state) {
    const { items } = state;
    const newItems = items.filter((item) => item.isAvailable === true);

    const subtotal = calcSubtotal(newItems);
    const total = calcTotal(subtotal);

    return {
        ...state,
        items: newItems,
        quantity: calcQuantity(newItems),
        subtotal,
        total,
    };
}

function removeAll(state) {
    const subtotal = calcSubtotal([]);
    const total = calcTotal(subtotal);

    return {
        ...state,
        items: [],
        quantity: calcQuantity([]),
        subtotal,
        total,
    };
}

function setUserData(state, payload) {
    const { items, lastItemId = 0 } = payload;

    const newItems = items || [];
    const subtotal = calcSubtotal(newItems);
    const quantity = calcQuantity(newItems);
    const total = calcTotal(subtotal);

    return {
        ...state,
        lastItemId,
        items: newItems,
        quantity,
        subtotal,
        total,
    };
}

function updateQuantities(state, quantities) {
    let needUpdate = false;

    const newItems = state.items.map((item) => {
        const quantity = quantities.find((x) => x.itemId === item.id && x.value !== item.quantity);

        if (!quantity) {
            return item;
        }

        needUpdate = true;

        return {
            ...item,
            quantity: quantity.value,
            total: quantity.value * item.price,
        };
    });

    if (needUpdate) {
        const subtotal = calcSubtotal(newItems);
        const total = calcTotal(subtotal);

        return {
            ...state,
            items: newItems,
            quantity: calcQuantity(newItems),
            subtotal,
            total,
        };
    }

    return state;
}

/*
* item example:
* {
*   id: 1,
*   product: {...}
*   options: [
*     {optionId: 1, optionTitle: 'Color', valueId: 1, valueTitle: 'Red'}
*   ],
*   price: 250,
*   quantity: 2,
*   total: 500
* }
* extraLine example:
* {
*   type: 'shipping',
*   title: 'Shipping',
*   price: 25
* }
*/
const initialState = {
    lastItemId: 0,
    quantity: 0,
    items: [],
    subtotal: 0,
    total: 0,
};

export default function cartReducer(state = initialState, action) {
    switch (action.type) {
        case CART_ADD_ITEM:
            return addItem(state, action.product, action.option, action.quantity, action.isAvailable);

        case CART_REMOVE_ITEM:
            return removeItem(state, action.itemId);

        case CART_REMOVE_ALL:
            return removeAll(state, action.itemId);

        case CART_REMOVE_EXPIRED:
            return removeExpiredItems(state);

        case CART_UPDATE_QUANTITIES:
            return updateQuantities(state, action.quantities);

        case CART_SET_USERDATA:
            return setUserData(state, action.payload);

        default:
            return state;
    }
}
